/**
 * Fetches data from the backend API.
 * This function automatically prepends the API base URL and adds the API key header.
 *
 * Why this is needed:
 * - The backend is hosted separately (e.g., on Render) and requires a full URL for API requests.
 * - The backend requires an 'x-api-key' header for authentication.
 *
 * Environment Variables:
 * - NEXT_PUBLIC_API_BASE_URL: The base URL of your backend API (e.g., https://your-backend.onrender.com).
 * - NEXT_PUBLIC_API_KEY: The API key required by your backend.
 *
 * How to set them:
 * 1. For local development:
 *    - Create a file named '.env.local' in the 'front_end' directory.
 *    - Add the following lines, replacing with your actual values:
 *      NEXT_PUBLIC_API_BASE_URL=http://localhost:8000 
 *      # Replace with your actual backend URL if not local
 *      NEXT_PUBLIC_API_KEY=your_secret_api_key_here
 *
 * 2. For Vercel deployment:
 *    - Go to your project settings on Vercel.
 *    - Navigate to "Environment Variables".
 *    - Add NEXT_PUBLIC_API_BASE_URL and NEXT_PUBLIC_API_KEY with their respective values.
 *    - Ensure these variables are available for all relevant environments (Production, Preview, Development).
 *
 * IMPORTANT: Never commit your .env.local file or your actual API key to version control.
 * The .env.local file should be listed in your .gitignore file.
 */
import { getSupabaseClient } from '@/lib/supabaseClient';

export async function apiFetch(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL;

  if (!baseUrl) {
    throw new Error(
      "API base URL is not configured. Please set NEXT_PUBLIC_API_BASE_URL."
    );
  }

  const url = `${baseUrl}${endpoint.startsWith("/") ? "" : "/"}${endpoint}`;

  const headers = new Headers(options.headers);
  // Attach Supabase access token if present
  try {
    const supabase = getSupabaseClient();
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (token) headers.set('Authorization', `Bearer ${token}`);
  } catch (e) {
    // Non-fatal: proceed without token; backend will 401
  }
  // Ensure Content-Type is set for POST/PUT requests if a body is present and no Content-Type is set
  if (options.body && !headers.has("Content-Type") && typeof options.body === 'string') {
      try {
          JSON.parse(options.body); // Check if it's a JSON string
          headers.append("Content-Type", "application/json");
      } catch (e) {
          // Not a JSON string, or already has a Content-Type (e.g. for FormData)
      }
  }


  const response = await fetch(url, {
    ...options,
    headers,
  });

  return response;
}

// Example of how to handle JSON responses (optional, can be done in the calling component)
export async function apiFetchJson<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await apiFetch(endpoint, options);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(errorData?.detail || `HTTP error! status: ${response.status}`);
  }
  return response.json() as Promise<T>;
} 